import { TWIDO_APP_NAME, TWIDO_LOGO_SRC } from "@/branding";
import { Button } from "@/components/ui/button";

const ERROR_COPY: Record<string, { title: string; description: string }> = {
  access_denied: {
    title: "Access denied",
    description:
      "Your account does not have access to this organization. Contact your administrator to request access.",
  },
  invalid_request: {
    title: "Couldn’t complete sign-in",
    description:
      "There is a problem with the authentication setup. Try again, or sign out and sign back in.",
  },
  unauthorized: {
    title: "Unauthorized",
    description:
      "You are not authorized to access this application. Verify your credentials or contact your administrator.",
  },
  login_required: {
    title: "Session expired",
    description: "Your session has expired. Sign in again to continue.",
  },
  consent_required: {
    title: "Consent required",
    description:
      "Additional permissions are required. Try signing in again.",
  },
  interaction_required: {
    title: "Additional verification needed",
    description: "Additional verification is required to complete sign-in.",
  },
  profile_load_failed: {
    title: "Couldn't load your account",
    description:
      "Signed in successfully but the app couldn't reach the Tideway API. Check your connection and try again.",
  },
};

const DEFAULT_COPY = {
  title: "Authentication error",
  description:
    "An unexpected error occurred during sign-in. Try again, or contact your administrator if it continues.",
};

interface AuthErrorPageProps {
  errorCode?: string;
  errorDescription?: string | null;
  onRetry: () => void;
  onLogout: () => void;
}

/** Full-screen Auth0 error — shown instead of retrying into a redirect loop. */
export function AuthErrorPage({
  errorCode,
  errorDescription,
  onRetry,
  onLogout,
}: AuthErrorPageProps) {
  const mapped = (errorCode && ERROR_COPY[errorCode]) || DEFAULT_COPY;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="flex w-full max-w-md flex-col items-center gap-5">
        <img
          src={TWIDO_LOGO_SRC}
          alt=""
          className="size-16 rounded-full shadow-[0_20px_60px_-20px_hsl(var(--neon-blue)/0.45)]"
        />
        <p className="text-xl font-semibold tracking-tight">{TWIDO_APP_NAME}</p>
        <div className="glass-panel-strong w-full p-6">
          <h1 className="text-lg font-semibold">{mapped.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mapped.description}
          </p>
          {errorDescription && (
            <p className="mt-4 font-mono text-xs text-destructive">
              {errorDescription}
            </p>
          )}
          {errorCode && (
            <p className="mt-2 font-mono text-xs text-muted-foreground">
              {errorCode}
            </p>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <Button type="button" onClick={onRetry}>
              Try again
            </Button>
            <Button type="button" variant="outline" onClick={onLogout}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
